/**
 * Turning remembered facts into something an image model can draw.
 *
 * Free's idea: prefetch from the lattice, then draw. The picture that comes out
 * should be shaped by what this mind actually remembers, so that swapping the
 * memory swaps the image the way it already swaps the answer.
 *
 * THE HARD PART IS RESTRAINT, not retrieval. A memory banner is prose, and most
 * of it is useless to an image model: clause structure, hedges, API names,
 * measurements. Janus is a 1B model whose image prompts want a short concrete
 * phrase, and pasting a paragraph of remembered text in front of it makes the
 * picture worse, not more grounded. So this extracts only the words that
 * describe how something LOOKS, and it is built to add nothing at all when the
 * lattice has nothing visual to say.
 *
 * That last property is the one worth defending. The web-developer lattice is
 * 9,886 facts of prose about APIs; asked to illustrate anything it should
 * contribute NOTHING and leave the visitor's prompt untouched, rather than
 * decorating a drawing with "asynchronous" and "deprecated". A grounding step
 * that cannot decline is a corruption step.
 *
 * No model call. This is a lexicon and a window, which means it is instant,
 * deterministic and testable, and it costs nothing on a page already holding
 * 2.4 GB of weights.
 */

/**
 * Words that describe appearance.
 *
 * Deliberately a closed list rather than a part-of-speech guess. An adjective
 * detector would happily pass "asynchronous", "deprecated" and "experimental",
 * which are adjectives about behaviour and mean nothing to a renderer.
 */
const VISUAL = {
  colour: [
    "red", "green", "blue", "yellow", "orange", "purple", "brown", "black",
    "white", "grey", "gray", "pink", "amber", "crimson", "scarlet", "violet",
    "indigo", "turquoise", "ochre", "russet", "golden", "gold", "silver",
    "copper", "bronze", "rust", "cream", "ivory", "olive", "emerald", "azure",
  ],
  /**
   * Intensity words. These MODIFY rather than denote, so a phrase made only of
   * them is dropped: "pale" on its own tells a renderer nothing.
   *
   * "deep" used to be here and was removed after it leaked "deep clone" out of
   * the web-developer lattice. No stop list saves that one, because "clone" is
   * an ordinary noun; the word simply has a dominant non-visual sense in
   * technical prose, as do "deep copy", "deep link" and "deep learning".
   */
  tone: ["pale", "dark", "bright", "dull", "vivid", "faded", "muted"],
  surface: [
    "smooth", "rough", "glossy", "matte", "fuzzy", "downy", "waxy", "ridged",
    "veined", "mottled", "speckled", "spotted", "striped", "cracked",
    "wrinkled", "furry", "hairy", "slimy", "powdery", "dusty", "wet", "damp",
    "dry", "translucent", "transparent", "opaque", "shiny", "fibrous", "grainy",
  ],
  form: [
    "round", "oval", "conical", "cylindrical", "flat", "curled", "coiled",
    "branching", "forked", "tapered", "lobed", "serrated", "toothed", "pointed",
    "blunt", "swollen", "shrivelled", "shriveled", "wilted", "drooping",
    "upright", "spindly", "stunted", "twisted", "hollow", "jagged",
  ],
  material: [
    "wooden", "wood", "metal", "metallic", "glass", "clay", "ceramic",
    "plastic", "stone", "paper", "leather", "cotton", "woollen", "resin",
    "filament", "rubber", "brass", "steel",
  ],
  light: [
    "sunlit", "shaded", "backlit", "glowing", "luminous", "dim", "gleaming",
    "shadowed", "misty", "hazy",
  ],
} as const;

const ALL_VISUAL = new Set<string>(Object.values(VISUAL).flat());

/**
 * Words never worth carrying into a picture, even next to a visual term.
 *
 * The time adverbs earn their place: "Affected seedlings look spindly and pale
 * before they collapse" produced the phrase "pale before", which is not a thing
 * anyone can draw.
 */
const STOP = new Set([
  "the", "a", "an", "and", "or", "of", "to", "in", "on", "at", "is", "are",
  "was", "were", "be", "been", "it", "its", "that", "this", "which", "with",
  "for", "as", "by", "from", "can", "may", "will", "should", "when", "if",
  "than", "then", "but", "not", "no", "such", "these", "those", "their",
  "before", "after", "while", "during", "until", "since", "often", "always",
  "usually", "sometimes", "also", "very", "quite", "more", "most", "less",
  "around", "near", "once", "still", "already", "become", "becomes", "look",
  "looks", "appear", "appears", "seem", "seems",
]);

const TONE = new Set<string>(VISUAL.tone);

const words = (t: string) =>
  t
    .toLowerCase()
    .replace(/[‘’]/g, "'")
    .replace(/[^\p{L}\p{N}'\s-]+/gu, " ")
    .split(/\s+/)
    .filter(Boolean);

/**
 * The meaning-carrying words of a piece of text, lightly normalised.
 *
 * Used to ask how much of an interpreted image prompt traces back to what was
 * actually remembered. Stop words are excluded because "the" and "of" appear in
 * everything and would push any two texts towards agreement.
 *
 * A crude plural strip rather than a stemmer: "seedlings" and "seedling" are
 * the same word for this purpose, and a real stemmer is a dependency and a
 * second thing to be wrong. It over-strips "grass" to "gras", which is harmless
 * here because BOTH sides are normalised the same way, so the pair still
 * matches. That property is what makes the shortcut safe, and it is the only
 * reason it is acceptable.
 */
export function contentWords(text: string): Set<string> {
  const out = new Set<string>();
  for (const w of words(text)) {
    if (w.length < 3 || STOP.has(w)) continue;
    out.add(w.endsWith("s") && !w.endsWith("ss") ? w.slice(0, -1) : w);
  }
  return out;
}

/**
 * Visual phrases in a piece of text: a look-word plus what it is describing.
 *
 * Takes up to two following content words, because "white fuzzy mould" and
 * "brown lesions at the stem" are what a renderer can use, while a bare "white"
 * is not. Trailing stop words are trimmed so a phrase never ends on "of".
 */
export function visualPhrases(text: string, maxWords = 3): string[] {
  const w = words(text);
  const out: string[] = [];
  for (let i = 0; i < w.length; i++) {
    if (!ALL_VISUAL.has(w[i])) continue;
    const phrase = [w[i]];
    for (let j = i + 1; j < w.length && phrase.length < maxWords; j++) {
      if (STOP.has(w[j])) break;
      phrase.push(w[j]);
      // A second look-word continues the phrase ("pale green"), anything else
      // is the noun and ends it.
      if (!ALL_VISUAL.has(w[j])) break;
    }
    while (phrase.length > 1 && STOP.has(phrase[phrase.length - 1])) phrase.pop();
    // A phrase made only of intensity words denotes nothing. "pale" alone is
    // not a subject, and it is what is left once "pale before" is trimmed.
    if (phrase.every((x) => TONE.has(x))) continue;
    out.push(phrase.join(" "));
  }
  return out;
}

/** Is `inner` a whole-word run inside `outer`? */
function containedIn(inner: string, outer: string): boolean {
  if (inner === outer) return true;
  return ` ${outer} `.includes(` ${inner} `);
}

export type GroundedPrompt = {
  /** What to hand the image model. */
  prompt: string;
  /** The visual detail the lattice contributed, in order. Empty is a real answer. */
  added: string[];
  /** True when the lattice had nothing to see and the prompt is untouched. */
  unchanged: boolean;
};

/**
 * Build an image prompt from the visitor's words plus what the mind remembers.
 *
 * The visitor's prompt always comes FIRST and is never rewritten. Remembered
 * detail is appended, never substituted, because a memory that can overrule the
 * request is a memory that draws the wrong thing confidently.
 *
 * Phrases already implied by the prompt are dropped, so asking for "a red
 * toadstool" does not produce "a red toadstool, red cap".
 */
export function buildImagePrompt(
  userPrompt: string,
  facts: string[],
  opts: { maxPhrases?: number; maxChars?: number } = {},
): GroundedPrompt {
  const maxPhrases = opts.maxPhrases ?? 4;
  const maxChars = opts.maxChars ?? 200;
  const base = userPrompt.trim();
  const already = new Set(words(base));

  const seen = new Set<string>();
  const added: string[] = [];
  for (const f of facts) {
    for (const p of visualPhrases(f)) {
      if (seen.has(p)) continue;
      // Skip a phrase whose every word the visitor already used.
      if (p.split(" ").every((x) => already.has(x))) continue;
      // Skip a phrase already contained in one we kept. The scan emits both
      // "white fuzzy growth" and "fuzzy growth" from the same sentence, and
      // listing the shorter one after the longer one just repeats the subject.
      if (added.some((k) => containedIn(p, k))) continue;
      seen.add(p);
      added.push(p);
      if (added.length >= maxPhrases) break;
    }
    if (added.length >= maxPhrases) break;
  }

  if (added.length === 0) return { prompt: base, added: [], unchanged: true };

  let prompt = `${base}, ${added.join(", ")}`;
  if (prompt.length > maxChars) {
    // Drop from the END, so the visitor's subject and the strongest remembered
    // detail survive the trim.
    const kept: string[] = [];
    for (const p of added) {
      const next = `${base}, ${[...kept, p].join(", ")}`;
      if (next.length > maxChars) break;
      kept.push(p);
    }
    if (kept.length === 0) return { prompt: base, added: [], unchanged: true };
    added.length = 0;
    added.push(...kept);
    prompt = `${base}, ${kept.join(", ")}`;
  }
  return { prompt, added, unchanged: false };
}

/* ---------------------------------------------------------------------------
 * The INTERPRETIVE path's pure half.
 *
 * `visualise.ts` asks the model to compose a scene; these two clean and score
 * the result. They live here rather than beside the model call because they
 * are ordinary functions of a string, and this is where the testable logic in
 * this bay lives. The scoring one is the honesty check on the whole approach,
 * so it belongs somewhere it will be tested rather than somewhere convenient.
 * ------------------------------------------------------------------------- */

/**
 * Strip the model's throat-clearing.
 *
 * Small instruction-tuned models open with "Sure!", wrap the answer in quotes,
 * or label it. None of that should reach an image model, where it would be
 * drawn as text or simply waste the prompt budget.
 */
export function cleanScene(raw: string, maxChars = 240): string {
  let s = raw.trim();
  s = s.replace(/^(sure|certainly|okay|ok|here('s| is))\b[^\n]*?[:,]\s*/i, "");
  s = s.replace(/^(image\s*)?prompt\s*[:\-]\s*/i, "");
  s = s.replace(/^picture\s*[:\-]\s*/i, "");
  // First paragraph only. Anything after a blank line is commentary.
  s = s.split(/\n\s*\n/)[0].split("\n")[0].trim();
  s = s.replace(/^["“'']+|["”'']+$/g, "").trim();
  if (s.length > maxChars) {
    // Cut at a comma or space rather than mid-word, so the tail of the prompt
    // is still a phrase.
    const cut = s.slice(0, maxChars);
    const at = Math.max(cut.lastIndexOf(", "), cut.lastIndexOf(" "));
    s = (at > maxChars * 0.6 ? cut.slice(0, at) : cut).trim().replace(/[,;:]$/, "");
  }
  return s;
}

/**
 * How much of this prompt traces back to what was remembered?
 *
 * Content words only, so "a", "the" and "of" cannot inflate it. This is the
 * honesty check on the interpretive path: it does not gate anything, it is
 * reported, because a low number is a real and interesting outcome rather than
 * an error. Some subjects genuinely have little in the lattice and the model
 * will say something lovely and unfounded, and the visitor should know which
 * they are looking at.
 */
export function sceneOverlap(prompt: string, facts: string[]): number {
  const p = contentWords(prompt);
  if (p.size === 0) return 0;
  const f = contentWords(facts.join(" "));
  let hit = 0;
  for (const w of p) if (f.has(w)) hit++;
  return hit / p.size;
}
